import { useTranslation } from 'next-i18next';
import DatePicker from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import { formatNumericValue, getUniqueOptions } from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import {
  CircularBgImage,
  ErrorMessage,
  InputField,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import EditableExpandableField from '@/shared/components/atoms/EditableExpandableField';

const Field: React.FC<{
  index: number;
  plotsList: IPeriodicTasks.IOperationPlotSchema[];
  handleRemove: () => void;
  handleAppend: () => void;
  onEdit: (toggleEdit: () => void) => void;
}> = ({ index, plotsList, handleRemove, handleAppend, onEdit }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<IPeriodicTasks.IOperationFormSchema>();

  const cropsIds = watch('crops');
  const fieldsWatcher = watch('plots');
  const selectedPlot = fieldsWatcher[index].plot;
  const workingAreaWatcher = watch(`plots.${index}.workingArea`);
  const startTimeWatcher = watch(`plots.${index}.startTime`);
  const endTimeWatcher = watch(`plots.${index}.endTime`);

  const uniqueOptions = getUniqueOptions<IPeriodicTasks.IOperationPlotSchema>({
    allOptions: plotsList ?? [],
    selectedOptions: fieldsWatcher.map(
      (field) => field.plot as IPeriodicTasks.IOperationPlotSchema
    ),
    getValue: (value) => value?.plotId,
    exclude: selectedPlot as IPeriodicTasks.IOperationPlotSchema,
  });

  return (
    <EditableExpandableField
      fieldArrayName='plots'
      addFieldText={t('add-field')}
      editFieldText={t('edit-field')}
      addAnotherFieldText={t('add-another-field')}
      index={index}
      length={fieldsWatcher.length}
      handleAppend={handleAppend}
      handleRemove={handleRemove}
      onEdit={onEdit}
      collapsedHeader={
        selectedPlot ? (
          <div className='flex items-center gap-8'>
            <CircularBgImage
              size='md'
              bgColor='bg-outline-default'
              imageClassName='scale-125'
              imageSrc={selectedPlot.cropImage as string}
            />
            <Typography>
              <span className='font-bold'>{selectedPlot.plotName}</span>
              <span className='text-surface-10'>
                {` (${formatNumericValue(Number(workingAreaWatcher))} ${t(
                  selectedPlot.areaUnit ?? ''
                )})`}
              </span>
            </Typography>
          </div>
        ) : undefined
      }
    >
      <SelectField
        id={`plots.${index}.plot`}
        errorMessageId={`plots.${index}.plot.plotId`}
        getLabel={(obj: IPeriodicTasks.IOperationPlotSchema) =>
          `(${obj.plotName} ${obj.plotArea} ${t(obj.areaUnit)})`
        }
        getValue={(obj: IPeriodicTasks.IOperationPlotSchema) => obj}
        value={plotsList.filter(
          (filter) => filter.plotId === fieldsWatcher[index]?.plot?.plotId
        )}
        borderColor={themeColors().outline.variant}
        label={t('Field')}
        placeholder={t('Field')}
        list={uniqueOptions}
        isDisabled={cropsIds?.length === 0}
        formatOptionLabel={(option) => (
          <div className='flex items-center gap-8'>
            <CircularBgImage imageSrc={option.cropImage} />
            {`${option.plotName} (${option.plotArea} ${t(option.areaUnit)})`}
          </div>
        )}
        py='0.5rem'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full min-w-0'
      />
      <InputField
        borderColor='border-outline-variant'
        width='w-full'
        type='number'
        py='!py-14'
        label={t('work-area')}
        placeholder={t('work-area')}
        error={!!errors?.plots?.[index]?.workingArea}
        errorMessage={errors?.plots?.[index]?.workingArea?.message}
        className='!rounded-xl bg-background-transparent'
        {...register(`plots.${index}.workingArea`)}
        suffix={<span>{t(plotsList?.[0]?.areaUnit ?? '')}</span>}
      />
      <div>
        <Typography weight='bold' className='mb-16'>
          {t('choose-start-end-operation')}
        </Typography>
        <div className='flex gap-16'>
          <Controller
            name={`plots.${index}.startTime`}
            control={control}
            render={({ field: { onChange } }) => (
              <DatePicker
                locale={language}
                onChange={(date) => {
                  if (date) onChange(date);
                }}
                placeholderText={t('startingTime')}
                showTimeSelect
                showTimeSelectOnly
                selected={
                  typeof startTimeWatcher === 'string'
                    ? new Date(startTimeWatcher)
                    : startTimeWatcher
                }
                required
                timeCaption='Time'
                dateFormat='h:mm aa'
                className='w-full cursor-pointer rounded-lg border-xs border-background-disable bg-white px-12 !py-14'
              />
            )}
          />
          <Controller
            name={`plots.${index}.endTime`}
            control={control}
            render={({ field: { onChange } }) => (
              <DatePicker
                locale={language}
                onChange={(date) => {
                  if (date) onChange(date);
                }}
                placeholderText={t('endingTime')}
                showTimeSelect
                showTimeSelectOnly
                required
                selected={
                  typeof endTimeWatcher === 'string'
                    ? new Date(endTimeWatcher)
                    : endTimeWatcher
                }
                timeCaption='Time'
                dateFormat='h:mm aa'
                className='w-full cursor-pointer rounded-lg border-xs border-background-disable bg-white px-12 !py-14'
              />
            )}
          />
        </div>
        {!!errors?.plots?.[index]?.endTime && (
          <ErrorMessage
            errorMessage={errors?.plots?.[index]?.endTime?.message ?? ''}
          />
        )}
      </div>
    </EditableExpandableField>
  );
};

export default Field;
